import { create } from "zustand";
import type { Pair, PairHours } from "@/types";
import { pairsApi } from "@/lib/api";
import { supabase } from "@/lib/supabase";

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unknown error";
}

/**
 * Pair Store
 * Správa párov a párových hodín pomocou Supabase
 */

interface PairStore {
  // State
  pairs: Pair[];
  activePairs: Pair[];
  selectedPair: Pair | null;
  pairHours: PairHours | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchPairs: () => Promise<void>;
  fetchActivePairs: () => Promise<void>;
  fetchPairHours: (pairId: number) => Promise<void>;
  selectPair: (pair: Pair | null) => void;
  createPair: (userAId: string, userBId: string) => Promise<Pair | null>;
  breakupPair: (pairId: number) => Promise<boolean>;
  clearPairs: () => void;

  // Realtime callbacks
  handlePairChange: () => void;
}

export const usePairStore = create<PairStore>((set, get) => ({
  // Initial state
  pairs: [],
  activePairs: [],
  selectedPair: null,
  pairHours: null,
  isLoading: false,
  error: null,

  /**
   * Načítať všetky páry
   */
  fetchPairs: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await pairsApi.list();

      if (response.success && response.data) {
        const pairs = response.data;
        set({
          pairs,
          activePairs: pairs.filter((p) => p.status === "active"),
        });
      } else {
        set({ error: response.error || "Failed to fetch pairs" });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : "Unknown error" });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Načítať iba aktívne páry
   */
  fetchActivePairs: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await pairsApi.list();

      if (response.success && response.data) {
        const pairs = response.data;
        set({ activePairs: pairs.filter((p) => p.status === "active") });
      } else {
        set({ error: response.error || "Failed to fetch active pairs" });
      }
    } catch (error) {
      // Graceful handling
      console.log("Pairs fetch error:", error);
      set({ activePairs: [] });
      if (error instanceof Error) {
        set({ error: error.message });
      }
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Načítať párové hodiny
   */
  fetchPairHours: async (pairId: number) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from("pair_hours")
        .select("*")
        .eq("pair_id", pairId)
        .single();

      if (error) throw error;

      set({ pairHours: data as PairHours });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : "Unknown error" });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Vybrať pár (pre detail view)
   */
  selectPair: (pair: Pair | null) => {
    set({ selectedPair: pair });
  },

  /**
   * Vytvoriť nový pár
   */
  createPair: async (userAId: string, userBId: string) => {
    set({ isLoading: true, error: null });
    const finish = () => set({ isLoading: false });
    try {
      const response = await pairsApi.create(userAId, userBId);

      if (response.success && response.data) {
        const newPair = response.data;

        // Pridať do zoznamu párov
        set((state) => ({
          pairs: [...state.pairs, newPair],
          activePairs: [...state.activePairs, newPair],
        }));

        finish();
        return newPair;
      }
      const errMsg = response.error ? response.error : "Failed to create pair";
      set({ error: errMsg });
      finish();
      return null;
    } catch (error) {
      set({ error: toErrorMessage(error) });
      finish();
      return null;
    }
  },

  /**
   * Rozpojiť pár
   */
  breakupPair: async (pairId: number) => {
    set({ isLoading: true, error: null });
    const finish = () => set({ isLoading: false });
    try {
      const response = await pairsApi.breakup(pairId);

      if (response.success) {
        // Aktualizovať stav páru
        set((state) => ({
          pairs: state.pairs.map((p) =>
            p.id === pairId ? { ...p, status: "inactive" as const } : p,
          ),
          activePairs: state.activePairs.filter((p) => p.id !== pairId),
        }));

        finish();
        return true;
      }
      const errMsg = response.error ? response.error : "Failed to breakup pair";
      set({ error: errMsg });
      finish();
      return false;
    } catch (error) {
      set({ error: toErrorMessage(error) });
      finish();
      return false;
    }
  },

  /**
   * Vyčistiť pair data
   */
  clearPairs: () => {
    set({
      pairs: [],
      activePairs: [],
      selectedPair: null,
      pairHours: null,
      error: null,
    });
  },

  /**
   * Realtime: Handler pre zmeny v pairs
   */
  handlePairChange: async () => {
    // Refresh pairs
    await get().fetchPairs();
  },
}));
